import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import { REGIONS, RATINGS, TYPES, YEARS, filterAndSortWorks, type FilterState } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 8;
const SORTS = ["最新", "最热", "评分"];

const CATEGORY_COPY: Record<string, { title: string; desc: string }> = {
  电影: { title: "电影分类", desc: "院线新片与口碑佳作，聚焦剧情结构、视听语言与角色弧光。" },
  电视剧: { title: "电视剧分类", desc: "古装权谋、都市情感与年代群像，长线叙事里的细节都值得细看。" },
  网络剧: { title: "网络剧分类", desc: "悬疑、轻喜剧与青春成长题材，短剧集的密度往往更高。" }
};

function FilterSelect({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-9 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
      </div>
    </label>
  );
}

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const typeFromUrl = params.get("type") || "全部类型";

  const [filters, setFilters] = useState<FilterState>({
    type: TYPES.includes(typeFromUrl as never) ? typeFromUrl : "全部类型",
    region: "全部地区",
    year: "全部年份",
    rating: "全部评分",
    sort: "最新"
  });

  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    if (key === "type") {
      const next = new URLSearchParams(params);
      if (value === "全部类型") {
        next.delete("type");
      } else {
        next.set("type", value);
      }
      setParams(next, { replace: true });
    }
  };

  const resetFilters = () => {
    setFilters({ type: "全部类型", region: "全部地区", year: "全部年份", rating: "全部评分", sort: "最新" });
    setPage(1);
    setParams(new URLSearchParams(), { replace: true });
  };

  const results = useMemo(() => filterAndSortWorks(filters), [filters]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const copy = CATEGORY_COPY[filters.type] || {
    title: "影视分类",
    desc: "覆盖电影、电视剧、网络剧等多种类型，按类型、地区、年份与评分快速筛选。"
  };

  useDocumentTitle(
    `${filters.type === "全部类型" ? "影视分类" : filters.type} - 亲吻姐姐影视剧资讯与剧情解析`,
    `亲吻姐姐影视分类页，${copy.desc} 可按地区、年份与评分筛选电影、电视剧与网络剧。`
  );

  const filterControls = (
    <>
      <FilterSelect label="类型" options={TYPES as unknown as string[]} value={filters.type} onChange={(v) => updateFilter("type", v)} />
      <FilterSelect label="地区" options={REGIONS} value={filters.region} onChange={(v) => updateFilter("region", v)} />
      <FilterSelect label="年份" options={YEARS} value={filters.year} onChange={(v) => updateFilter("year", v)} />
      <FilterSelect label="评分等级" options={RATINGS} value={filters.rating} onChange={(v) => updateFilter("rating", v)} />
    </>
  );

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12 lg:px-8">
          <Breadcrumb items={[{ label: "影视分类" }]} />
          <h1 className="mt-5 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">{copy.title}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">{copy.desc}</p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10 lg:px-8">
        <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="hidden grid-cols-2 gap-4 md:grid lg:grid-cols-5">
            {filterControls}
            <FilterSelect label="排序" options={SORTS} value={filters.sort} onChange={(v) => updateFilter("sort", v)} />
          </div>

          <div className="flex items-center justify-between gap-3 md:hidden">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800"
            >
              <i className="ri-equalizer-line"></i>
              筛选
            </button>
            <FilterSelect label="排序" options={SORTS} value={filters.sort} onChange={(v) => updateFilter("sort", v)} />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="font-heading text-primary-700">{results.length}</strong> 部作品
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>

        {results.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-movie-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">未找到相关影视作品</h3>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              当前筛选条件下没有匹配的内容，试试放宽筛选条件或重置筛选后重新浏览。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination current={safePage} total={totalPages} onChange={setPage} />
          </>
        )}
      </section>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] flex items-end bg-background-950/50 md:hidden" onClick={() => setMobileFilterOpen(false)}>
          <div className="w-full rounded-t-2xl bg-background-50 p-5" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">{filterControls}</div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50"
              >
                查看 {results.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}